import { BasketItem } from '@/modules/checkout/api';
import { ProductListItem } from '@/modules/product/api/productList';
import { CategoryHomePath } from '@/modules/product/api/singleProduct';

import { clientCookies } from '../../../shared/utils/client-cookies';
import { convertImportFlagToString } from '../../../shared/utils/convert-import-flag-to-string';
import { dataLayer } from './lib/data-layer';

// move all event to each file per function

export interface AddProductType {
  id: string;
  name: string;
  currency: string;
  brand: string;
  categoryPath: CategoryHomePath[];
  price: number;
  quantity: number;
  importFlag?: boolean;
  attributionToken?: string | null;
}

const formatBasketItems = (items: Omit<BasketItem, 'vatRate'>[]) => {
  return items.map((item) => {
    const itemObject: Record<string, string | number> = {
      item_id: item.productId,
      item_name: item.productName,
      item_brand: item.brandName,
      price: item.price.amount / 100,
      quantity: item.quantity,
      import: convertImportFlagToString(item.product?.importFlag),
    };

    item.categoryPath.forEach((category, index) => {
      itemObject[`item_category${index > 0 ? index + 1 : ''}`] = category;
    });

    return itemObject;
  });
};

const formatAddProductsItems = (items: AddProductType[]) => {
  let value = 0;

  const formattedItems = items.map((item) => {
    value += (item.price * item.quantity) / 100;

    const itemObject: Record<string, string | number> = {
      item_id: item.id,
      item_name: item.name,
      item_brand: item.brand,
      price: item.price / 100,
      quantity: item.quantity,
      import: convertImportFlagToString(item.importFlag),
    };

    if (item.attributionToken) {
      itemObject.attributionToken = item.attributionToken;
    }

    item.categoryPath.forEach((category, index) => {
      itemObject[`item_category${index > 0 ? index + 1 : ''}`] = category.name;
    });

    return itemObject;
  });

  return { formattedItems, value };
};

const formatListItemsForViewItemList = (
  items: {
    productId: string;
    name: string;
    brandName: string;
    price: number;
    importFlag: boolean;
    categoryHomePath: { name: string }[];
  }[],
  pager: { page: number; limit: number }
) => {
  if (!items?.length) {
    return [];
  }

  return items.map((item, index) => {
    const itemObject: Record<string, string | number> = {
      index: index + (pager.page - 1) * pager.limit,
      item_id: item.productId,
      item_name: item.name,
      item_brand: item.brandName,
      price: item.price / 100,
      import: convertImportFlagToString(item.importFlag),
    };

    item.categoryHomePath.forEach((category, index) => {
      itemObject[`item_category${index > 0 ? index + 1 : ''}`] = category.name;
    });

    return itemObject;
  });
};

export const formatListItemsForSelectItem = (
  items: {
    productId: string;
    name: string;
    brandName: string;
    price: number;
    importFlag?: boolean;
    categoryHomePath: { name: string }[];
    attributionToken?: string | null;
  }[],
  currency: string,
  itemIndex: number,
  pager: { page: number; limit: number }
) => {
  if (!items?.length) {
    return [];
  }

  return items.map((item) => {
    const itemObject: Record<string, string | number> = {
      index: itemIndex + (pager.page - 1) * pager.limit,
      item_id: item.productId,
      item_name: item.name,
      item_brand: item.brandName,
      price: item.price / 100,
      currency,
      import: convertImportFlagToString(item.importFlag),
    };

    if (item.attributionToken) {
      itemObject.attributionToken = item.attributionToken;
    }

    item.categoryHomePath.forEach((category, index) => {
      itemObject[`item_category${index > 0 ? index + 1 : ''}`] = category.name;
    });

    return itemObject;
  });
};

export const virtualPageView = (
  url: string,
  title: string,
  pathName: string,
  pageReferrer: string
) => {
  const dataLayerObject = {
    event: 'virtualPageview',
    pageUrl: url,
    pageTitle: title,
    pagePath: pathName,
    pageReferrer,
  };

  dataLayer(dataLayerObject);
};

export const viewPromotion = (
  promotionName: string,
  creativeSlot: string,
  creativeName?: string | null,
  products?: {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id: string;
    item_list_name: string;
    price: number;
    import: string;
  }[]
) => {
  const dataLayerObject: Record<string, any> = {
    event: 'view_promotion',
    eccomerce: {
      promotion_name: promotionName,
      creative_slot: creativeSlot,
    },
  };

  if (products) {
    dataLayerObject.eccomerce.items = products;
  }

  if (creativeName) {
    dataLayerObject.eccomerce.creative_name = creativeName;
  }

  dataLayer(dataLayerObject);
};

export const selectPromotion = (
  promotionName: string,
  creativeSlot: string,
  creativeName?: string | null,
  product?: {
    item_id: string;
    item_name: string;
    index: number;
    item_brand: string;
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
    item_list_id: string;
    item_list_name: string;
    price: number;
    import: string;
  }
) => {
  const dataLayerObject: Record<string, any> = {
    event: 'select_promotion',
    eccomerce: {
      promotion_name: promotionName,
      creative_slot: creativeSlot,
    },
  };

  if (product) {
    dataLayerObject.eccomerce.items = [product];
  }

  if (creativeName) {
    dataLayerObject.eccomerce.creative_name = creativeName;
  }

  dataLayer(dataLayerObject);
};

export const addToCartEvent = (items: AddProductType[]) => {
  const { formattedItems, value } = formatAddProductsItems(items);

  const dataLayerObject: Record<string, any> = {
    event: 'add_to_cart',
    ecommerce: {
      currency: items[0].currency,
      value,
      items: formattedItems,
    },
  };

  dataLayer(dataLayerObject);
};

export const viewProductListPageEvent = (
  items: ProductListItem[],
  listId: string,
  listName: string,
  currency: string,
  pager: {
    page: number;
    limit: number;
  } = { page: 1, limit: 1 }
) => {
  const formattedItem = formatListItemsForViewItemList(items, pager);
  const dataLayerObject: Record<string, any> = {
    event: 'view_item_list',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      currency: currency,
      items: formattedItem,
    },
  };

  //TODO better event queuing
  //virtualPageview event must be first so timeout this event for correct order
  setTimeout(() => {
    dataLayer(dataLayerObject);
  }, 300);
};

export const clickProductListPageEvent = (
  item: {
    productId: string;
    name: string;
    brandName: string;
    price: number;
    importFlag?: boolean;
    categoryHomePath: { name: string }[];
  },
  listId: string,
  listName: string,
  currency: string,
  itemIndex: number,
  pager: { page: number; limit: number } = { page: 1, limit: 1 }
) => {
  const formattedItem = formatListItemsForSelectItem(
    [item],
    currency,
    itemIndex,
    pager
  );

  const dataLayerObject: Record<string, any> = {
    event: 'select_item',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: formattedItem,
    },
  };

  dataLayer(dataLayerObject);
};

export const clickProductSearchPageEvent = (
  item: {
    productId: string;
    name: string;
    brandName: string;
    price: number;
    importFlag?: boolean;
    categoryHomePath: { name: string }[];
  },
  listId: string,
  listName: string,
  currency: string,
  itemIndex: number,
  pager: { page: number; limit: number } = { page: 1, limit: 1 }
) => {
  const formattedItem = formatListItemsForSelectItem(
    [item],
    currency,
    itemIndex,
    pager
  );

  const dataLayerObject: Record<string, any> = {
    event: 'product_select',
    ecommerce: {
      item_list_id: listId,
      item_list_name: listName,
      items: formattedItem,
    },
  };

  dataLayer(dataLayerObject);
};

export const viewProductPageEvent = (
  currency: string,
  items: Omit<BasketItem, 'vatRate'>[]
) => {
  const formattedItem = formatBasketItems(items);
  delete formattedItem[0].quantity;

  const dataLayerObject: Record<string, any> = {
    event: 'view_item',
    ecommerce: {
      currency: currency,
      value: formattedItem[0].price,
      items: formattedItem,
    },
    bing_facebook_data: {
      currency: currency,
      value: formattedItem[0].price,
      items: formattedItem,
    },
  };

  //TODO better event queuing
  //virtualPageview event must be first so timeout this event for correct order
  setTimeout(() => {
    dataLayer(dataLayerObject);
  }, 300);
};

export const deleteItemFromCartEvent = (product: {
  id: string;
  currency: string;
  price: number;
  name: string;
  brand: string;
  amount: number;
  categoryPath: string[];
  importFlag?: boolean;
}) => {
  const dataLayerObject: Record<string, any> = {
    event: 'remove_from_cart',
    ecommerce: {
      currency: product.currency,
      value: (product.price * product.amount) / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_brand: product.brand,
          price: product.price / 100,
          quantity: product.amount,
          import: convertImportFlagToString(product.importFlag),
        },
      ],
    },
  };

  product.categoryPath.forEach((category, index) => {
    dataLayerObject.ecommerce.items[0][
      `item_category${index > 0 ? index + 1 : ''}`
    ] = category;
  });

  dataLayer(dataLayerObject);
};

export const viewCartPageEvent = (
  totalPrice: number,
  currency: string,
  items: BasketItem[]
) => {
  const dataLayerObject: Record<string, any> = {
    event: 'view_cart',
    ecommerce: {
      currency,
      value: totalPrice / 100,
      items: formatBasketItems(items),
    },
  };

  //TODO better event queuing
  //virtualPageview event must be first so timeout this event for correct order
  setTimeout(() => {
    dataLayer(dataLayerObject);
  }, 300);
};

export const viewCheckoutPageEvent = (
  totalPrice: number,
  currency: string,
  paymentMethod: string,
  shippingMethod: string,
  items: BasketItem[]
) => {
  const beginCheckoutObject: Record<string, any> = {
    event: 'begin_checkout',
    ecommerce: {
      currency,
      value: totalPrice / 100,
      items: formatBasketItems(items),
    },
  };

  const shippingInfoObject: Record<string, any> = {
    event: 'add_shipping_info',
    ecommerce: {
      shipping_tier: shippingMethod,
      currency,
      value: totalPrice / 100,
      items: formatBasketItems(items),
    },
  };

  const paymentInfoObject: Record<string, any> = {
    event: 'add_payment_info',
    ecommerce: {
      payment_type: paymentMethod,
      currency,
      value: totalPrice / 100,
      items: formatBasketItems(items),
    },
  };

  //TODO better event queuing
  //virtualPageview event must be first so timeout this event for correct order
  setTimeout(() => {
    dataLayer(beginCheckoutObject);

    dataLayer(shippingInfoObject);

    dataLayer(paymentInfoObject);
  }, 300);
};

export const viewSuccessPageEvent = (
  orderId: string,
  totalPrice: number,
  tax: number,
  shippingPrice: number,
  currency: string,
  shippingTier: string,
  paymentType: string,
  items: BasketItem[],
  googleAds: {
    userProvidedData: {
      email: string;
      phone: string;
      name: string;
      street: string;
      city: string;
      postalCode: string;
    };
    additionalData: {
      discount: number;
      est_deliv_date: string;
    };
  }
) => {
  const cookieKey = `finesio_gtm_purchase_${orderId}`;
  if (clientCookies(cookieKey) === orderId) return;

  const dataLayerObject = {
    event: 'purchase',
    ecommerce: {
      shipping_tier: shippingTier,
      payment_type: paymentType,
      transaction_id: orderId,
      value: totalPrice / 100,
      tax: tax,
      shipping: shippingPrice / 100,
      currency: currency,
      coupon: '',
      items: formatBasketItems(items),
    },
    googleAds: googleAds,
  };

  //TODO better event queuing
  //virtualPageview event must be first so timeout this event for correct order
  setTimeout(() => {
    dataLayer(dataLayerObject);
  }, 300);

  clientCookies(cookieKey, orderId);
};
